import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import DashboardLayout from '../components/DashboardLayout';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import { MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX } from '../constants/muiNativeTableDenseLayout';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import { api, buildDropshipPriorityRequestBody, endpoints } from '../api/config';

const STATUS_CONFIG = [
  { key: 'active', label: 'Active', color: 'primary.main' },
  { key: 'unpicked', label: 'Unpicked', color: 'error.main' },
  { key: 'unassigned', label: 'Ready to pick', color: 'warning.main' },
  { key: 'unavailable', label: 'Unavailable', color: 'error.main' },
];

const renderStatusChip = (value) => {
  if (!value) return '-';
  return <Chip label={value} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />;
};
const renderCountChip = (value) => (
  <Chip label={value ?? 0} size="small" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
);
const renderPickedChip = (value) => {
  if (!value) return <Chip label="Pending" size="small" color="warning" variant="outlined" />;
  const val = String(value).toLowerCase();
  if (val === 'picked') return <Chip label="Picked" size="small" color="success" variant="outlined" />;
  return <Chip label={value} size="small" color="default" variant="outlined" />;
};
const renderInvChip = (value) => {
  const available = value === true || String(value).toLowerCase() === 'true';
  return <Chip label={available ? 'Available' : 'Not Available'} size="small" color={available ? 'success' : 'error'} variant="outlined" />;
};

const buildColumns = (statusKey) => ([
  { accessorKey: 'ponumber', header: 'PO Number'},
  { accessorKey: 'sonumber', header: 'SO Number'},
  { accessorKey: 'internal_id', header: 'Internal ID'},
  { accessorKey: 'username', header: 'Picker', cell: ({ getValue }) => (statusKey === 'unassigned' ? '-' : (getValue() || '-')) },
  { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => renderStatusChip(getValue()) },
  { accessorKey: 'items_count', header: 'Items', cell: ({ getValue }) => renderCountChip(getValue()) },
]);
const buildItemColumns = () => ([
  { accessorKey: 'item', header: 'Item'},
  { accessorKey: 'quantity', header: 'Qty'},
  { accessorKey: 'location', header: 'Location'},
  { accessorKey: 'is_picked', header: 'Picked', cell: ({ getValue }) => renderPickedChip(getValue()) },
  { accessorKey: 'username', header: 'Picker'},
  { accessorKey: 'inv_available', header: 'Inventory', cell: ({ getValue }) => renderInvChip(getValue()) },
]);
const buildPickSequenceColumns = () => ([
  { accessorKey: 'item', header: 'Item'},
  { accessorKey: 'quantity', header: 'Qty'},
  { accessorKey: 'total_qty', header: 'Total Qty', cell: ({ row, getValue }) => renderCountChip(getValue() ?? row?.original?.total_quantity ?? row?.original?.quantity ?? 0) },
  { accessorKey: 'internal_id', header: 'Internal ID'},
  { accessorKey: 'transaction_id', header: 'Transaction ID'},
  { accessorKey: 'customer_name', header: 'Customer'},
  { accessorKey: 'ship_date', header: 'Ship Date'},
  { accessorKey: 'priority', header: 'Priority'},
]);

const REGULAR_PICK_PROGRESS_LOCATION_STORAGE_KEY = 'regularPickProgressSelectedLocation';
const REGULAR_GRID_PAGE_SIZE = 1000;

const PriorityEditorCell = React.memo(function PriorityEditorCell({ initialValue, onCommit, inputKey }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <TextField
        key={inputKey}
        size="small"
        type="number"
        defaultValue={String(initialValue ?? 0)}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => onCommit(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        inputProps={{ min: 0, step: 1 }}
        sx={{ width: 84, '& .MuiInputBase-input': { py: 0.6 } }}
      />
    </Box>
  );
});

const RegularPickProgress = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pickSeqLoading, setPickSeqLoading] = useState(false);
  const [pickSeqError, setPickSeqError] = useState('');
  const [pickSeqData, setPickSeqData] = useState([]);
  const [pickSeqLocation, setPickSeqLocation] = useState(() => localStorage.getItem(REGULAR_PICK_PROGRESS_LOCATION_STORAGE_KEY) || 'L60-Hardeeville_SC');
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [data, setData] = useState({ active: [], unassigned: [], unpicked: [], unavailable: [], total_records: 0 });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsItems, setDetailsItems] = useState([]);
  const [detailsTitle, setDetailsTitle] = useState('');
  const [detailsView, setDetailsView] = useState('item');
  const [globalSearch, setGlobalSearch] = useState('');
  const [unpickedSearch, setUnpickedSearch] = useState('');
  const [unavailableSearch, setUnavailableSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [unassignedSearch, setUnassignedSearch] = useState('');
  const [detailsSearch, setDetailsSearch] = useState('');
  const [pickSeqSearch, setPickSeqSearch] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saveLoading, setSaveLoading] = useState(false);

  const activeRef = useRef(null);
  const unassignedRef = useRef(null);
  const unpickedRef = useRef(null);
  const unavailableRef = useRef(null);
  const pickSequenceRef = useRef(null);
  const pendingPriorityByKeyRef = useRef(new Map());
  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();

  const filterItems = (items, searchTerm) => {
    const hasLocal = searchTerm && searchTerm.trim() !== '';
    const hasGlobal = globalSearch && globalSearch.trim() !== '';
    if (!hasLocal && !hasGlobal) return items;
    const localTerm = hasLocal ? searchTerm.toLowerCase().trim() : null;
    const globalTerm = hasGlobal ? globalSearch.toLowerCase().trim() : null;
    return items.filter((item) =>
      Object.values(item).some((value) => {
        if (value === null || value === undefined) return false;
        const v = String(value).toLowerCase();
        return (localTerm ? v.includes(localTerm) : false) || (globalTerm ? v.includes(globalTerm) : false);
      })
    );
  };

  const itemGridColumns = useMemo(() => buildItemColumns().map((col) => ({
    ...col,
    cell: col.cell || ((info) => {
      const raw = info.row.original[col.accessorKey];
      return raw === null || raw === undefined || raw === '' ? '-' : String(raw);
    }),
  })), []);
  const getPickSequenceRowKey = useCallback((row) => {
    if (!row) return '';
    if (row?._pickSeqIndex !== undefined) return `index:${row._pickSeqIndex}`;
    if (row?.unique_id) return `unique:${String(row.unique_id)}`;
    return `fallback:${String(row?.internal_id || '')}|${String(row?.item || '')}|${String(row?.transaction_id || '')}`;
  }, []);

  const updatePickSequencePriority = useCallback((row, nextValue) => {
    const normalized =
      nextValue === '' || nextValue === null || nextValue === undefined ? 0 : Number(nextValue);
    const safePriority = Number.isFinite(normalized) ? normalized : 0;
    const rowKey = getPickSequenceRowKey(row);
    if (!rowKey) return;
    pendingPriorityByKeyRef.current.set(rowKey, safePriority);
  }, [getPickSequenceRowKey]);

  const pickSeqGridColumns = useMemo(() => buildPickSequenceColumns().map((col) => {
    if (col.accessorKey === 'priority') {
      return {
        ...col,
        cell: (info) => {
          const rowKey = getPickSequenceRowKey(info.row.original);
          const currentValue =
            pendingPriorityByKeyRef.current.get(rowKey) ?? info.getValue() ?? 0;
          return (
            <PriorityEditorCell
              inputKey={`${rowKey}-${currentValue}`}
              initialValue={currentValue}
              onCommit={(nextPriority) => updatePickSequencePriority(info.row.original, nextPriority)}
            />
          );
        },
      };
    }
    return {
      ...col,
      cell: col.cell || ((info) => {
        const raw = info.row.original[col.accessorKey];
        return raw === null || raw === undefined || raw === '' ? '-' : String(raw);
      }),
    };
  }), [getPickSequenceRowKey, updatePickSequencePriority]);
  const poGridColumns = (statusKey) => buildColumns(statusKey).map((col) => ({
    ...col,
    cell: col.cell || ((info) => {
      const raw = info.row.original[col.accessorKey];
      return raw === null || raw === undefined || raw === '' ? '-' : String(raw);
    }),
  }));
  const withStableRowIds = (rows, prefix) =>
    (Array.isArray(rows) ? rows : []).map((row, index) => {
      const baseId =
        row?.id ??
        row?._id ??
        row?.internal_id ??
        row?.transaction_id ??
        `${row?.item ?? row?.ponumber ?? 'row'}`;
      return {
        ...row,
        // Always append index so repeated backend ids don't collide in React/DataGrid keys.
        id: `${prefix}-${String(baseId)}-${index}`,
      };
    });
  const toDataGridColumns = (columns) =>
    columns.map((col) => ({
      field: col.accessorKey,
      headerName: col.header,
      sortable: true,
      resizable: col.resizable !== false,
      flex: col.flex ?? (col.width ? undefined : 1),
      width: col.width,
      minWidth: col.minWidth ?? col.minSize ?? col.size ?? 80,
      renderCell: col.cell
        ? (params) =>
            col.cell({
              getValue: () => params.row?.[col.accessorKey],
              row: { original: params.row },
            })
        : (params) => {
            const raw = params.row?.[col.accessorKey];
            return raw === null || raw === undefined || raw === '' ? '-' : String(raw);
          },
    }));

  const summary = useMemo(() => {
    const sumQty = (arr) => (arr || []).reduce((s, i) => s + (parseInt(i?.quantity || 0, 10) || 0), 0);
    return {
      active: data.active?.length || 0,
      unassigned: data.unassigned?.length || 0,
      unassignedQty: sumQty(data.unassigned),
      unpicked: data.unpicked?.length || 0,
      unpickedQty: sumQty(data.unpicked),
      unavailable: data.unavailable?.length || 0,
      unavailableQty: sumQty(data.unavailable),
    };
  }, [data]);

  const pickSeqSummary = useMemo(() => {
    const items = Array.isArray(pickSeqData) ? pickSeqData : [];
    return { count: items.length, qty: items.reduce((s, i) => s + (parseInt(i?.quantity ?? i?.qty ?? 0, 10) || 0), 0) };
  }, [pickSeqData]);

  const toggleSection = (k) => setCollapsedSections((prev) => ({ ...prev, [k]: !prev[k] }));
  const openSection = (k) => setCollapsedSections((prev) => ({ ...(prev || {}), [k]: false }));
  const scrollToSection = (ref) => ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  useEffect(() => {
    try { const saved = localStorage.getItem('regularPickProgressCollapsedSections'); if (saved) setCollapsedSections(JSON.parse(saved)); } catch {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('regularPickProgressCollapsedSections', JSON.stringify(collapsedSections)); } catch {}
  }, [collapsedSections]);
  useEffect(() => { if (pickSeqLocation) localStorage.setItem(REGULAR_PICK_PROGRESS_LOCATION_STORAGE_KEY, pickSeqLocation); }, [pickSeqLocation]);

  const fetchLocations = async () => {
    setLocationsLoading(true);
    try {
      const resp = await api.get(endpoints.locations);
      const raw = resp?.data;
      // Robust normalization of the payload to an array
      const list = Array.isArray(raw)
        ? raw
        : (Array.isArray(raw?.data) ? raw.data
          : (raw?.data && typeof raw.data === 'object'
            ? Object.values(raw.data)
            : (Array.isArray(raw?.results) ? raw.results
              : (Array.isArray(raw?.records) ? raw.records
                : (typeof raw === 'object' ? Object.values(raw) : [])))));
      
      const values = new Set();
      list.forEach((loc) => {
        if (loc && typeof loc === 'object') {
          // Pick the best available field for the location name
          const val = loc.location || loc.name || loc.location_name || loc.locationName || loc.id || loc.location_id || loc.internal_id;
          if (val) values.add(String(val));
        } else if (loc) {
          values.add(String(loc));
        }
      });
      const options = Array.from(values).sort((a, b) => a.localeCompare(b));
      setLocations(options);
      if (options.length > 0 && !options.includes(pickSeqLocation)) setPickSeqLocation(options[0]);
    } catch (e) {
      console.error('Failed to fetch locations:', e);
    } finally { setLocationsLoading(false); }
  };

  const fetchData = async (location) => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (location) params.append('location', location);
      const resp = await api.get(`${endpoints.dropshipPickProgress}?${params.toString()}`);
      const payload = resp?.data || {};
      const assignedRaw = payload.assigned || payload.active || [];
      const activeData = Array.isArray(assignedRaw) ? assignedRaw.filter((item) => item && (item.ponumber || item.sonumber || item.internal_id)) : [];
      const allItems = [...activeData];
      const rawUnassigned = Array.isArray(payload.not_completed || payload.unassigned) ? (payload.not_completed || payload.unassigned) : [];
      rawUnassigned.forEach((record) => Array.isArray(record.data) && allItems.push(...record.data));
      const unpickedItems = allItems.filter((item) => item?.is_picked === '');
      const unavailableItems = allItems.filter((item) => item?.is_picked === '' && item?.inv_available === false);
      const unassignedItems = allItems.filter((item) => item?.is_picked === '' && item?.inv_available === true);
      setData({ total_records: payload.total_records || 0, active: activeData, unassigned: unassignedItems, unpicked: unpickedItems, unavailable: unavailableItems });
    } catch (e) { setError(e?.response?.data?.message || e?.message || 'Failed to load pick progress'); } finally { setLoading(false); }
  };
  const fetchPickSequence = async (location) => {
    if (!location) return;
    setPickSeqLoading(true); setPickSeqError('');
    try {
      const resp = await api.get(endpoints.dropshipNextItems, { params: { location } });
      const payload = resp?.data;
      const rawItems = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : Array.isArray(payload?.data) ? payload.data : payload ? [payload] : [];
      const items = rawItems.map((item, index) => ({ ...item, _pickSeqIndex: index }));
      pendingPriorityByKeyRef.current.clear();
      setPickSeqData(items);
    } catch (e) { setPickSeqError(e?.response?.data?.message || e?.message || 'Failed to load pick sequence'); setPickSeqData([]); } finally { setPickSeqLoading(false); }
  };
  const handleSavePickSeqOrder = async () => {
    const payload = (Array.isArray(pickSeqData) ? pickSeqData : [])
      .filter((item) => item.internal_id)
      .map((item) => {
        const pending = pendingPriorityByKeyRef.current.get(getPickSequenceRowKey(item));
        return { internal_id: item.internal_id, priority: pending ?? item.priority ?? 0 };
      });
    if (payload.length === 0) return setSnackbar({ open: true, message: 'No items with internal_id found to update priorities.', severity: 'warning' });
    setSaveLoading(true);
    try {
      await api.post(endpoints.dropshipUpdatePriority, buildDropshipPriorityRequestBody(payload));
      await fetchPickSequence(pickSeqLocation);
      setSnackbar({ open: true, message: 'Priorities updated successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.message || err?.message || 'Failed to update priorities', severity: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };
  useEffect(() => { fetchLocations(); }, []);
  useEffect(() => {
    if (pickSeqLocation) {
      fetchData(pickSeqLocation);
      fetchPickSequence(pickSeqLocation);
    }
  }, [pickSeqLocation]);
  useEffect(() => {
    if (sessionStorage.getItem('pickSequenceNeedsRefresh') === 'true' && pickSeqLocation) {
      sessionStorage.removeItem('pickSequenceNeedsRefresh');
      fetchPickSequence(pickSeqLocation);
    }
  }, [pickSeqLocation]);

  const openDetails = (title, items, view = 'item') => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsSearch('');
    setDetailsView(view);
    setDetailsTitle(title);
    setDetailsItems(Array.isArray(items) ? items : []);
    setDetailsLoading(false);
  };

  const renderSectionGrid = (key, label) => {
    const items = key === 'active' ? data.active : key === 'unassigned' ? data.unassigned : key === 'unpicked' ? data.unpicked : data.unavailable;
    const searchTerm = key === 'active' ? activeSearch : key === 'unassigned' ? unassignedSearch : key === 'unpicked' ? unpickedSearch : unavailableSearch;
    const filtered = filterItems(items || [], searchTerm);
    if (!filtered.length) return <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>{(items || []).length === 0 ? 'No data available.' : 'No items match your search.'}</Box>;
    return (
      <Box sx={{ flex: statusFilter !== 'all' ? 1 : 'none', minHeight: 0, width: '100%' }}>
        <HoverExpandDataGridPro
          rows={withStableRowIds(filtered, `regular-${key}`)}
          columns={toDataGridColumns(itemGridColumns)}
          getRowId={(row) => String(row.id)}
          disableVirtualization
          rowSelection={false}
          rowSelectionModel={[]}
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          pagination
          paginationMode="client"
          pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
          initialState={{ pagination: { paginationModel: { pageSize: REGULAR_GRID_PAGE_SIZE, page: 0 } } }}
          rowHeight={denseMode ? 36 : 52}
          columnHeaderHeight={denseMode ? 40 : 56}
          onRowClick={key === 'unassigned' ? (params) => {
            const row = params.row;
            const allItems = Array.isArray(row.data) ? row.data : [];
            const details = allItems.filter((item) => {
              const isPicked = item?.is_picked;
              const isPending = isPicked === '' || isPicked === null || isPicked === undefined || String(isPicked).toLowerCase() === 'false' || isPicked === false;
              if (!isPending) return false;
              const invAvailable = item?.inv_available;
              return invAvailable === true || String(invAvailable).toLowerCase() === 'true';
            });
            if (details.length) openDetails(`${label} • PO ${row?.ponumber || 'N/A'}`, details);
          } : undefined}
          sx={{
            height: statusFilter === 'all' ? 400 : '100%',
            flex: statusFilter !== 'all' ? 1 : 'none',
            minHeight: statusFilter !== 'all' ? 0 : 'auto',
            maxHeight: statusFilter === 'all' ? '70vh' : 'none',
          }}
        />
      </Box>
    );
  };

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: statusFilter !== 'all' ? '100%' : 'auto', minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 1 }, mt: { xs: 1, md: 1 }, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, flexShrink: 0 }}>
          {statusFilter !== 'all' && <IconButton onClick={() => setStatusFilter('all')} sx={{ mr: -1 }} aria-label="go back to cards view"><ArrowBackIcon /></IconButton>}
          <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>Regular Pick Progress</Typography>
          <TextField label="Global Search" placeholder="Search across all tables..." value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} size="small" InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ minWidth: 200, maxWidth: 260, mt: { xs: 1, md: 0.5 } }} />
          <FormControl size="small" sx={{ minWidth: 190, mt: { xs: 1, md: 0.5 } }}>
            <InputLabel id="regular-pick-progress-location-label" shrink={!!pickSeqLocation}>Location</InputLabel>
            <Select labelId="regular-pick-progress-location-label" value={pickSeqLocation} label="Location" onChange={(e) => setPickSeqLocation(e.target.value)}>
              {(locationsLoading ? [pickSeqLocation].filter(Boolean) : (locations.length ? locations : [pickSeqLocation].filter(Boolean))).map((loc) => <MenuItem key={loc} value={loc}>{loc}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150, mt: { xs: 1, md: 0.5 } }}>
            <InputLabel id="regular-pick-progress-status-label" shrink={!!statusFilter}>Status</InputLabel>
            <Select labelId="regular-pick-progress-status-label" value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>{STATUS_CONFIG.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}<MenuItem value="pickSequence">Pick Sequence</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ ...MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX, mt: { xs: 1, md: 0.5 } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={loading || pickSeqLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => {
                if (!pickSeqLocation) return;
                fetchData(pickSeqLocation);
                fetchPickSequence(pickSeqLocation);
              }}
              disabled={!pickSeqLocation || loading || pickSeqLoading}
              sx={{ height: 32, px: 1.5, minWidth: 80 }}
            >
              Refresh
            </Button>
            <GlobalDenseModeToolbar denseMode={denseMode} denseModeTogglePending={denseModeTogglePending} persistDenseMode={persistDenseMode} />
          </Box>
        </Box>

        {error && <Box sx={{ mb: 2, color: 'error.main', flexShrink: 0 }}>{error}</Box>}
        <Box sx={{ flex: statusFilter !== 'all' ? 1 : 'none', minHeight: statusFilter !== 'all' ? 0 : 'auto', display: 'flex', flexDirection: 'column', overflow: statusFilter !== 'all' ? 'hidden' : 'visible' }}>
          {(statusFilter === 'all' || statusFilter === 'pickSequence') && (
            <Box ref={pickSequenceRef} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, backgroundColor: 'background.paper', display: 'flex', flexDirection: 'column', flex: statusFilter === 'pickSequence' ? 1 : 'none', minHeight: statusFilter === 'pickSequence' ? 0 : 'auto' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 2, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => toggleSection('pickSequence')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'primary.main' }} /><Typography variant="h6" sx={{ fontWeight: 600 }}>Pick Sequence</Typography><Typography variant="body2" sx={{ color: 'text.secondary' }}>({pickSeqSummary.count} lines, Qty: {pickSeqSummary.qty})</Typography></Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField size="small" placeholder="Search pick sequence..." value={pickSeqSearch} onClick={(e) => e.stopPropagation()} onChange={(e) => setPickSeqSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ maxWidth: 260 }} />
                  <Button
                    variant="contained"
                    size="small"
                    disabled={saveLoading}
                    startIcon={saveLoading ? <CircularProgress size={14} color="inherit" /> : null}
                    onClick={(e) => { e.stopPropagation(); handleSavePickSeqOrder(); }}
                  >
                    {saveLoading ? 'Saving...' : 'Save order'}
                  </Button>
                </Box>
              </Stack>
              {collapsedSections.pickSequence ? null : (
                pickSeqError ? <Box sx={{ p: 2, color: 'error.main', textAlign: 'center' }}>{pickSeqError}</Box> : (
                  <HoverExpandDataGridPro
                    rows={withStableRowIds(filterItems(pickSeqData, pickSeqSearch), 'regular-pickseq')}
                    columns={toDataGridColumns(pickSeqGridColumns)}
                    loading={pickSeqLoading}
                    getRowId={(row) => String(row.id)}
                    disableVirtualization
                    rowSelection={false}
                    rowSelectionModel={[]}
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
                    initialState={{ pagination: { paginationModel: { pageSize: REGULAR_GRID_PAGE_SIZE, page: 0 } } }}
                    rowHeight={denseMode ? 36 : 52}
                    columnHeaderHeight={denseMode ? 40 : 56}
                    onRowClick={(params) => openDetails(`Pick Sequence • ${params.row?.ponumber || 'N/A'}`, [params.row], 'pickSequence')}
                    sx={{
                      height: statusFilter === 'all' ? 400 : '100%',
                      flex: statusFilter === 'pickSequence' ? 1 : 'none',
                      minHeight: statusFilter === 'pickSequence' ? 0 : 'auto',
                      maxHeight: statusFilter === 'all' ? '70vh' : 'none',
                    }}
                  />
                )
              )}
            </Box>
          )}

          {(statusFilter === 'all' ? STATUS_CONFIG : STATUS_CONFIG.filter((s) => s.key === statusFilter)).map(({ key, label, color }) => {
            const searchTerm = key === 'active' ? activeSearch : key === 'unassigned' ? unassignedSearch : key === 'unpicked' ? unpickedSearch : unavailableSearch;
            const setSearch = key === 'active' ? setActiveSearch : key === 'unassigned' ? setUnassignedSearch : key === 'unpicked' ? setUnpickedSearch : setUnavailableSearch;
            const ref = key === 'active' ? activeRef : key === 'unassigned' ? unassignedRef : key === 'unpicked' ? unpickedRef : unavailableRef;
            const qty = key === 'unassigned' ? summary.unassignedQty : key === 'unpicked' ? summary.unpickedQty : key === 'unavailable' ? summary.unavailableQty : undefined;
            return (
              <Box key={key} ref={ref} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, backgroundColor: 'background.paper', display: 'flex', flexDirection: 'column', flex: statusFilter !== 'all' ? 1 : 'none', minHeight: statusFilter !== 'all' ? 0 : 'auto' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 2, flexShrink: 0, cursor: 'pointer' }} onClick={() => toggleSection(key)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} /><Typography variant="h6" sx={{ fontWeight: 600 }}>{label}</Typography></Box>
                  <TextField size="small" placeholder={key === 'unassigned' ? 'Search orders...' : 'Search items...'} value={searchTerm} onClick={(e) => e.stopPropagation()} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ maxWidth: 300 }} />
                </Stack>
                {collapsedSections[key] ? null : renderSectionGrid(key, label)}
                {qty !== undefined && !collapsedSections[key] ? <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>Qty: {qty}</Typography> : null}
              </Box>
            );
          })}
        </Box>

        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 5 }}>
              <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>{detailsTitle || 'Items'}</Typography>
              <TextField size="small" placeholder="Search items..." value={detailsSearch} onChange={(e) => setDetailsSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ maxWidth: 300 }} />
            </Box>
            <IconButton aria-label="close" onClick={() => setDetailsOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {detailsLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}><CircularProgress /></Box> : (
              <HoverExpandDataGridPro
                rows={withStableRowIds(filterItems(detailsItems, detailsSearch), 'regular-details')}
                columns={toDataGridColumns(detailsView === 'pickSequence' ? pickSeqGridColumns : itemGridColumns)}
                getRowId={(row) => String(row.id)}
                disableVirtualization
                rowSelection={false}
                rowSelectionModel={[]}
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                pagination
                paginationMode="client"
                pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
                initialState={{ pagination: { paginationModel: { pageSize: REGULAR_GRID_PAGE_SIZE, page: 0 } } }}
                rowHeight={denseMode ? 36 : 52}
                columnHeaderHeight={denseMode ? 40 : 56}
                sx={{ maxHeight: '60vh' }}
              />
            )}
          </DialogContent>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default RegularPickProgress;
